import RootState from '../../../../../app/appStore';
import { makeSelectFilteredRowsByTableId, makeSelectNonGroupColumns, makeSelectRowById, makeSelectTableColumnsByTableId, makeSelectTableStateByTableId, selectFlattenedColumns } from '../selectors';
import { BaseColumn, Cell, CellDimensions, Column, Row, SelectedColumn, SummaryColumn, ValueType } from '../types';

import TableStyle, { MIN_SCROLLBAR_HANDLE_SIZE, ROW_HEIGHT, SCROLLBAR_WIDTH } from './constants';

/**
 * 특정 셀의 화면상 위치와 크기를 계산하는 함수
 * 
 * @param canvasRef - Canvas 요소에 대한 참조
 * @param tableId - 테이블의 고유 식별자
 * @param rowIndex - 계산할 셀의 행 인덱스
 * @param columnIndex - 계산할 셀의 열 인덱스
 * @returns CellDimensions | null - 셀의 위치와 크기 정보 또는 계산 불가능한 경우 null
 * 
 * @description
 * 1. 셀의 위치가 유효한지 검증
 * 2. 현재 스크롤 위치를 고려하여 셀이 화면에 보이는지 확인
 * 3. 보이는 셀에 대해서만 위치와 크기를 계산
 * 4. 계산된 값은 캔버스 상의 실제 픽셀 좌표
 */
export const getCellDimensions = (
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    tableId: string,
    rowIndex: number,
    columnIndex: number
): CellDimensions | null => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());
    const columns = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);

    if (
        rowIndex < 0 ||
        rowIndex >= rows.length ||
        columnIndex < 0 ||
        columnIndex >= columns.length
    ) {
        return null;
    }

    const canvas = canvasRef.current;
    if (!canvas) return null;

    const HEADER_HEIGHT = calculateHeaderHeight(tableState.columns);
    const tableHeight = canvas.clientHeight || 0;

    // 보이는 행 계산
    const startRowIndex = Math.floor(tableState.scrollY / ROW_HEIGHT);
    const visibleRowCount = Math.ceil((tableHeight - HEADER_HEIGHT) / ROW_HEIGHT);
    const relativeRowIndex = rowIndex - startRowIndex;

    if (relativeRowIndex < 0 || relativeRowIndex >= visibleRowCount) {
        return null; // 행이 보이지 않는 경우
    }

    // 셀의 Y 위치 계산
    const y = relativeRowIndex * ROW_HEIGHT + HEADER_HEIGHT;
    const columnWidths = columns.map(col => col.width);

    // 셀의 X 위치 계산
    const x = columnWidths.slice(0, columnIndex).reduce((acc, width) => acc + width, 0) - tableState.scrollX;

    // 셀의 너비와 높이 계산
    const width = (columnWidths[columnIndex] || 0) + 1;
    const height = ROW_HEIGHT + 1;

    return { x, y, width, height };
};

/**
 * 마우스 클릭 위치에 해당하는 셀의 행과 열 인덱스를 계산하는 함수
 * 
 * @param x - 마우스의 X 좌표
 * @param y - 마우스의 Y 좌표
 * @param tableId - 테이블의 고유 식별자
 * @param tableHeight - 테이블의 전체 높이
 * @returns {{ rowIndex: number, columnIndex: number } | null} - 셀의 위치 정보 또는 유효하지 않은 위치인 경우 null
 */
export const getCellPosition = (
    x: number,
    y: number,
    tableId: string,
    tableHeight: number
): { rowIndex: number, columnIndex: number } | null => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());

    // summaryColumn 영역 확인 로직 추가
    if (tableState.hasSummaryColumn) {
        const maxDepth = calculateSummaryColumnMaxDepth(Object.values(tableState.summaryColumns)) + 1;
        const summaryHeight = maxDepth * ROW_HEIGHT;
        const summaryY = tableHeight - SCROLLBAR_WIDTH - summaryHeight;

        // 클릭 위치가 summary 영역인 경우 null 반환
        if (y >= summaryY) {
            return null;
        }
    }

    /**
     * 중첩된 그룹 컬럼 구조를 평탄화하여 일반 컬럼 배열로 변환하는 함수
     * 
     * @param columns - 처리할 컬럼 배열
     * @returns Column[] - 평탄화된 컬럼 배열
     */
    // GROUP이 아닌 모든 컬럼들을 순서대로 가져옴
    const nonGroupColumns = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);

    const HEADER_HEIGHT = calculateHeaderHeight(tableState.columns);
    const ROW_HEIGHT_PIXELS = ROW_HEIGHT;

    // 헤더 영역 클릭 시 null 반환 (헤더는 별도로 처리)
    if (y < HEADER_HEIGHT) return null;

    // 스크롤 위치를 고려하여 실제 행 인덱스 계산
    const startRowIndex = Math.floor(tableState.scrollY / ROW_HEIGHT_PIXELS);
    const visibleRowCount = Math.ceil((tableHeight - HEADER_HEIGHT) / ROW_HEIGHT_PIXELS);
    // 클릭 위치의 상대적 행 인덱스 계산 (-2는 테두리 보정값)
    const relativeRowIndex = Math.floor((y - HEADER_HEIGHT - 2) / ROW_HEIGHT_PIXELS);

    // 유효하지 않은 행 인덱스 검사
    if (relativeRowIndex >= rows.length) return null;
    if (relativeRowIndex < 0) return null;

    // 실제 행 인덱스와 해당 행 데이터 가져오기
    const rowIndex = startRowIndex + relativeRowIndex;
    const row = rows[rowIndex];
    // 행이 존재하지 않거나 보이는 영역을 벗어난 경우 null 반환
    if (row === undefined || relativeRowIndex >= visibleRowCount) return null;

    // 수평 스크롤을 고려한 절대 X 좌표 계산
    const absoluteX = x + tableState.scrollX;
    let accumulatedWidth = 0;
    let columnIndex = -1;

    // 클릭 위치에 해당하는 컬럼 찾기
    for (let i = 0; i < nonGroupColumns.length; i++) {
        const column = nonGroupColumns[i];
        if (!column) continue;

        accumulatedWidth += column.width;
        if (absoluteX < accumulatedWidth) {
            columnIndex = i;
            break;
        }
    }

    // 유효한 컬럼을 찾지 못한 경우 null 반환
    if (columnIndex === -1) return null;

    return { rowIndex, columnIndex };
};

/**
 * 입력된 키가 출력 가능한 문자인지 확인하는 함수
 * 
 * @param key - 검사할 키 문자열
 * @returns boolean - 출력 가능한 문자이면 true, 아니면 false
 * 
 * 정규식 패턴:
 * - \p{L}: 모든 유니코드 문자 (알파벳)
 * - \p{N}: 모든 숫자
 * - \p{P}: 모든 구두점
 * - \p{S}: 모든 기호
 */
export const isPrintableKey = (key: string) => {
    return /^\p{L}$|^\p{N}$|^\p{P}$|^\p{S}$/u.test(key);
};

/**
 * 마우스 이벤트의 캔버스 내 상대 좌표를 계산하는 함수
 * 
 * @param e - 마우스 이벤트 객체
 * @param canvasRef - 캔버스 요소에 대한 참조
 * @returns {{ x: number, y: number }} - 캔버스 내 상대 좌표
 */
export const getMousePosition = (
    e: MouseEvent,
    canvasRef: React.RefObject<HTMLCanvasElement | null>
): { x: number; y: number } => {
    let rect;
    // 이벤트 대상이 Element인 경우
    if (e.currentTarget instanceof Element) {
        rect = e.currentTarget.getBoundingClientRect();
    }
    // 캔버스 참조가 있는 경우
    else if (canvasRef.current) {
        rect = canvasRef.current.getBoundingClientRect();
    }
    // 둘 다 없는 경우 기본값 사용
    else {
        rect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
    }

    // 전역 좌표에서 캔버스의 위치를 빼서 상대 좌표 계산
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    return { x, y };
};

/**
 * 모든 행의 선택 상태를 초기화하는 함수
 * 
 * @param rows - 초기화할 행 배열
 * @returns Row[] - 선택 상태가 초기화된 새로운 행 배열
 */
export const resetRowsSelection = (rows: Row[]): Row[] => {
    return rows.map(row => ({
        ...row,
        selected: false,
        data: Object.keys(row.data).reduce((acc, key) => {
            acc[key] = { ...row.data[key], selected: false };
            return acc;
        }, {} as { [key: string]: Cell })
    }));
};

/**
 * 단일 행의 선택 상태를 초기화하는 함수
 * 
 * @param row - 초기화할 행 객체
 * @returns Row - 선택 상태가 초기화된 새로운 행 객체
 */
export const resetRowSelection = (row: Row): Row => {
    return {
        ...row,
        selected: false,
        data: Object.keys(row.data).reduce((acc, key) => {
            acc[key] = {
                ...row.data[key],
                selected: false
            };
            return acc;
        }, {} as { [key: string]: Cell })
    };
};

/**
 * 테이블의 스크롤 위치를 조정하는 함수
 * 
 * @param rowIndex - 대상 행 인덱스
 * @param columnIndex - 대상 열 인덱스
 * @param tableId - 테이블 식별자
 * @param canvasRef - Canvas 요소에 대한 참조
 * @returns {{ newScrollY: number, newScrollX: number }} 새로운 스크롤 위치
 */
export const adjustScroll = (
    rowIndex: number,
    columnIndex: number,
    tableId: string,
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
) => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const nonGroupColumns = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);

    if (!canvasRef.current) return { newScrollY: tableState.scrollY, newScrollX: tableState.scrollX };

    let newScrollY = tableState.scrollY;
    let newScrollX = tableState.scrollX;

    // 헤더 높이 계산
    const headerHeight = calculateHeaderHeight(tableState.columns);

    // 캔버스 높이에서 헤더와 스크롤바, 요약 행을 제외한 실제 테이블 영역 높이 계산
    const canvasHeight = canvasRef.current.clientHeight;
    const tableHeight = canvasHeight - headerHeight - SCROLLBAR_WIDTH - (tableState.hasSummaryColumn ? ROW_HEIGHT : 0);

    // 보이는 행 계산
    const visibleStartRowIndex = Math.floor(tableState.scrollY / ROW_HEIGHT);
    const visibleRowCount = Math.floor(tableHeight / ROW_HEIGHT);
    const visibleEndRowIndex = visibleStartRowIndex + visibleRowCount - 1;

    // 대상 컬럼의 시작 위치와 끝 위치 계산
    const targetColumnStartPosition = nonGroupColumns.slice(0, columnIndex).reduce((acc, column) => acc + column.width, 0);
    const targetColumnEndPosition = nonGroupColumns.slice(0, columnIndex + 1).reduce((acc, column) => acc + column.width, 0);
    const availableWidth = canvasRef.current.clientWidth - SCROLLBAR_WIDTH;

    // 수직 스크롤 조정
    if (rowIndex < visibleStartRowIndex) {
        // 위로 스크롤이 필요한 경우
        newScrollY = rowIndex * ROW_HEIGHT;
    } else if (rowIndex > visibleEndRowIndex) {
        // 아래로 스크롤이 필요한 경우
        newScrollY = (rowIndex - visibleRowCount + 1) * ROW_HEIGHT;
    }

    // 수평 스크롤 조정
    if (targetColumnStartPosition < tableState.scrollX) {
        // 왼쪽으로 스크롤이 필요한 경우
        newScrollX = targetColumnStartPosition;
    } else if (targetColumnEndPosition > tableState.scrollX + availableWidth) {
        // 오른쪽으로 스크롤이 필요한 경우
        newScrollX = targetColumnEndPosition - availableWidth;
    }

    // 스크롤 범위 제한
    newScrollY = Math.max(0, Math.min(newScrollY, tableState.maxScrollY));
    newScrollX = Math.max(0, Math.min(newScrollX, tableState.maxScrollX));

    return { newScrollY, newScrollX };
};

/**
 * 수직 스크롤바의 크기와 위치를 계산하는 함수
 * 
 * @param tableId - 테이블의 고유 식별자
 * @param canvasRef - Canvas 요소에 대한 참조
 * @returns {{
 *   scrollbarHeight: number, // 스크롤바의 전체 높이
 *   handleHeight: number,    // 스크롤바 핸들의 높이
 *   handleY: number         // 스크롤바 핸들의 Y 좌표 위치
 * }}
 * 
 * @description
 * 1. 테이블의 전체 콘텐츠 높이와 보이는 영역의 높이를 비교하여 스크롤바 크기를 계산
 * 2. 스크롤바 핸들의 크기는 보이는 영역과 전체 콘텐츠의 비율에 따라 결정
 * 3. 핸들의 최소 크기는 MIN_SCROLLBAR_HANDLE_SIZE로 제한
 * 4. 현재 스크롤 위치에 따라 핸들의 Y 좌표를 계산
 */
export const getVerticalScrollbarBounds = (
    tableId: string,
    canvasRef: React.RefObject<HTMLCanvasElement | null>
) => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());

    const canvas = canvasRef.current;
    if (!canvas) return { scrollbarHeight: 0, handleHeight: 0, handleY: 0 };

    const canvasHeight = canvas.clientHeight;
    const headerHeight = calculateHeaderHeight(tableState.columns);
    const summaryHeight = tableState.hasSummaryColumn ?
        (calculateSummaryColumnMaxDepth(Object.values(tableState.summaryColumns)) + 1) * ROW_HEIGHT : 0;

    // 헤더 높이를 포함한 전체 컨텐츠 높이 계산
    const totalContentHeight = rows.length * ROW_HEIGHT + headerHeight;

    // 스크롤바 높이 계산 - 가로 스크롤바가 있을 때만 SCROLLBAR_WIDTH를 뺌
    const horizontalScrollbarSpace = tableState.hasHorizontalScrollbar ? SCROLLBAR_WIDTH : 0;
    const scrollbarHeight = canvasHeight - headerHeight - summaryHeight - horizontalScrollbarSpace;

    const visibleRatio = Math.min(1, scrollbarHeight / Math.max(totalContentHeight, scrollbarHeight));
    const handleHeight = Math.max(scrollbarHeight * visibleRatio, MIN_SCROLLBAR_HANDLE_SIZE);

    // 스크롤 핸들 위치 계산 시 headerHeight 사용
    const handleY = tableState.maxScrollY > 0
        ? (tableState.scrollY / tableState.maxScrollY) * (scrollbarHeight - handleHeight) + headerHeight
        : headerHeight;

    return {
        scrollbarHeight,
        handleHeight,
        handleY,
    };
}

/**
 * 수평 스크롤바의 크기와 위치를 계산하는 함수
 * 
 * @param tableId - 테이블의 고유 식별자
 * @param canvasRef - Canvas 요소에 대한 참조
 * @returns {{
 *   scrollbarWidth: number, // 스크롤바의 전체 너비
 *   handleWidth: number,    // 스크롤바 핸들의 너비
 *   handleX: number         // 스크롤바 핸들의 X 좌표 위치
 * }}
 * 
 * @description
 * 1. 테이블의 전체 콘텐츠 너비와 보이는 영역의 너비를 비교하여 스크롤바 크기를 계산
 * 2. 스크롤바 핸들의 크기는 보이는 영역과 전체 콘텐츠의 비율에 따라 결정
 * 3. 핸들의 최소 크기는 MIN_SCROLLBAR_HANDLE_SIZE로 제한
 * 4. 현재 스크롤 위치에 따라 핸들의 X 좌표를 계산
 */
export const getHorizontalScrollbarBounds = (
    tableId: string,
    canvasRef: React.RefObject<HTMLCanvasElement | null>
) => {
    // 테이블 상태와 컬럼 정보 가져오기
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const columns = makeSelectTableColumnsByTableId(tableId)(RootState.getState());

    // Canvas가 없는 경우 기본값 반환
    const canvas = canvasRef.current;
    if (!canvas) return { scrollbarWidth: 0, handleWidth: 0, handleX: 0 };

    // Canvas의 너비와 전체 콘텐츠 너비 계산
    const canvasWidth = canvas.clientWidth;
    const totalContentWidth =
        (Object.values(columns) as any[]).reduce((acc: number, column: any) => acc + column.width, 0) +
        SCROLLBAR_WIDTH +
        1;

    // 스크롤바 너비 계산 - 세로 스크롤바가 있을 때만 SCROLLBAR_WIDTH를 뺌
    const verticalScrollbarSpace = tableState.hasVerticalScrollbar ? SCROLLBAR_WIDTH : 0;
    const scrollbarWidth = canvasWidth - verticalScrollbarSpace;

    // 보이는 영역과 전체 콘텐츠의 비율 계산
    const visibleRatio = (scrollbarWidth) / totalContentWidth;

    // 스크롤바 핸들의 너비 계산 (최소 크기 제한 적용)
    const handleWidth = Math.max(scrollbarWidth * visibleRatio, MIN_SCROLLBAR_HANDLE_SIZE);

    // 현재 스크롤 위치에 따른 핸들의 X 좌표 계산
    const handleX = tableState.maxScrollX > 0
        ? (tableState.scrollX / tableState.maxScrollX) * (scrollbarWidth - handleWidth)
        : 0;

    return {
        scrollbarWidth,
        handleWidth,
        handleX,
    };
}

/**
 * 테이블의 전체 콘텐츠 영역의 크기를 계산하는 함수
 * 
 * @param tableId - 테이블의 고유 식별자
 * @returns {{
 *   contentWidth: number, // 전체 콘텐츠의 너비 (모든 컬럼 너비의 합 + 스크롤바 너비)
 *   contentHeight: number // 전체 콘텐츠의 높이 (모든 행의 높이 합 + 헤더 높이)
 * }}
 * 
 * @description
 * - contentWidth는 모든 컬럼의 너비를 합산하고 스크롤바 너비를 더한 값
 * - contentHeight는 (행 개수 + 1) * 행 높이로 계산 (1은 헤더를 위한 추가 행)
 */
export const getContentBounds = (tableId: string): { contentWidth: number, contentHeight: number } => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());

    if (!tableState) return { contentWidth: 0, contentHeight: 0 };

    // 모든 컬럼의 너비를 합산하고 스크롤바 너비를 더함
    const contentWidth = (Object.values(tableState.columns) as any[]).reduce((acc: number, column: any) => acc + column.width, 0) + SCROLLBAR_WIDTH;

    // 전체 행 수에 헤더 행을 더해 높이 계산
    const contentHeight = (rows.length + 1) * ROW_HEIGHT;

    return { contentWidth, contentHeight };
};

/**
 * Canvas에 테이블 셀을 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 셀의 X 좌표
 * @param y - 셀의 Y 좌표
 * @param width - 셀의 너비
 * @param height - 셀의 높이
 * @param text - 셀에 표시할 텍스트
 * @param color - 텍스트 색상
 * 
 * @description
 * 1. 셀의 테두리를 검은색으로 그림
 * 2. 지정된 색상으로 텍스트를 셀 내부에 렌더링
 * 3. 텍스트는 셀의 왼쪽에서 10px 떨어진 위치에서 시작하며, 수직 중앙에 정렬됨
 */
export const drawCell = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    text: string,
    color: string
) => {
    ctx.strokeStyle = '#000';
    ctx.strokeRect(x, y, width, height);
    ctx.fillStyle = color;
    ctx.fillText(text, x + 10, y + height / 2);
};

export const formatValue = (value: any, format?: string): string => {
    
    // null, undefined 값 처리를 먼저 확인
    if (value === null || value === undefined) return '';
    
    if (!format) return String(value);

    // :가 있는 경우에만 분리
    const [formatType, formatParam] = format.split(':');
    if (value === '' || value === undefined || value === null) return '';

    switch (formatType) {
        case 'currency':
            return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'USD' }).format(value);
        case 'percent':
            return new Intl.NumberFormat('ko-KR', { style: 'percent' }).format(value);
        case 'decimal':
            return new Intl.NumberFormat('ko-KR', { style: 'decimal' }).format(value);
        case 'locale':
            return new Intl.NumberFormat('ko-KR').format(value);
        case 'date':
            return new Date(value).toLocaleDateString('ko-KR');
        case 'time':
            return new Date(value).toLocaleTimeString('ko-KR');
        case 'datetime':
            return new Date(value).toLocaleString('ko-KR');
        case 'padded':
            const padLength = parseInt(formatParam, 10);
            return String(value).padStart(padLength, '0');
        default:
            return String(value);
    }
};


/**
 * 마우스 위치에 해당하는 컬럼을 찾고 리사이즈 핸들 근처인지 확인하는 함수
 * 
 * @param x - 마우스의 X 좌표
 * @param y - 마우스의 Y 좌표
 * @param columns - 컬럼 정보를 담고 있는 객체 맵
 * @param scrollX - 수평 스크롤 위치
 * @returns SelectedColumn - 선택된 컬럼 정보 ({id: string, grabbed: boolean})
 */
export const getSelectedColumn = (
    x: number,
    y: number,
    columns: { [columnIndex: number]: Column },
    scrollX: number
): SelectedColumn => {
    /**
     * 컬럼의 너비를 반환하는 헬퍼 함수
     */
    const getColumnWidth = (column: Column): number => {
        return column.width;
    };

    /**
     * 주어진 위치에 있는 컬럼을 재귀적으로 찾는 함수
     * 
     * @param x - 마우스 X 좌표
     * @param y - 마우스 Y 좌표
     * @param columnsArr - 검사할 컬럼 배열
     * @param depth - 현재 깊이 레벨 (중첩된 그룹 컬럼의 깊이)
     * @param accumulatedX - 누적된 X 좌표 (이전 컬럼들의 너비 합)
     * @param accumulatedY - 누적된 Y 좌표 (상위 컬럼들의 높이 합)
     * @returns {
     *   column: 찾은 컬럼 또는 null,
     *   grabbed: 리사이즈 핸들 근처 여부,
     *   accX: 현재까지의 누적 X 좌표
     * }
     */

    const findColumnAtPosition = (
        x: number,
        y: number,
        columnsArr: Column[],
        depth: number = 0,
        accumulatedX: number = 0,
        accumulatedY: number = 0
    ): { column: Column | null; grabbed: boolean; accX: number } => {
        for (const column of columnsArr) {
            // visible이 false인 컬럼은 건너뛰기
            if (column.visible === false) continue;

            const columnWidth = getColumnWidth(column);
            const columnStartX = accumulatedX;
            const columnEndX = columnStartX + columnWidth;
            const rowSpan = column.rowspan || 1;

            // 컬럼의 세로 범위 계산
            const startY = accumulatedY;
            const endY = startY + ROW_HEIGHT * rowSpan;

            // 마우스가 컬럼의 X축 범위 내에 있는지 확인
            const isWithinXRange = x >= columnStartX - scrollX && x <= columnEndX - scrollX;
            // 마우스가 리사이즈 핸들 근처(3px 이내)에 있는지 확인
            const isNearResizeHandle = Math.abs(x - (columnEndX - scrollX)) < 3;

            if (column.type === ValueType.GROUP && column.children) {
                // 그룹 컬럼인 경우
                if (y >= startY && y < endY) {

                    if (isNearResizeHandle) {
                        return { column, grabbed: true, accX: columnEndX };
                    }
                    if (isWithinXRange) {
                        return { column, grabbed: false, accX: columnEndX };
                    }
                }

                if (isWithinXRange) {
                    const childResult = findColumnAtPosition(
                        x,
                        y,
                        column.children,
                        depth + 1,
                        columnStartX,
                        startY + (rowSpan * ROW_HEIGHT)
                    );

                    if (childResult.column) {
                        return childResult;
                    }
                }
            } else {
                // 일반 컬럼인 경우
                if (y >= startY && y < endY) {
                    if (isNearResizeHandle) {
                        return { column, grabbed: true, accX: columnEndX };
                    }
                    if (isWithinXRange) {
                        return { column, grabbed: false, accX: columnEndX };
                    }
                }
            }

            accumulatedX = columnEndX;
        }
        return { column: null, grabbed: false, accX: accumulatedX };
    };

    // 최상위 컬럼들에 대해 검사 수행
    const { column, grabbed } = findColumnAtPosition(x, y, Object.values(columns));

    return {
        id: column?.id || '',
        grabbed
    };
};

/**
 * 정렬 타입을 순환하는 함수
 * 
 * @param currentOrderType - 현재 정렬 타입 ('asc', 'desc', undefined)
 * @returns 다음 정렬 타입
 * 
 * @description
 * 정렬 타입은 다음 순서로 순환됩니다:
 * undefined -> 'desc' -> 'asc' -> undefined
 * 
 * @example
 * toggleOrderType(undefined) // returns 'desc'
 * toggleOrderType('desc')   // returns 'asc'
 * toggleOrderType('asc')    // returns undefined
 */
export const toggleOrderType = (currentOrderType?: string) => {
    switch (currentOrderType) {
        case undefined:
            return 'desc';
        case 'desc':
            return 'asc';
        default:
            return undefined;
    }
}

/**
 * 현재 셀 위치에서 다음 셀의 위치를 계산하는 함수
 * 
 * @param currentRowIndex - 현재 행 인덱스
 * @param currentColumnIndex - 현재 열 인덱스
 * @param totalColumns - 전체 열 개수
 * @param totalRows - 전체 행 개수
 * @param autoAddRow - 마지막 행에서 자동으로 새 행 추가 여부
 * @returns {{
 *   nextRowIndex: number,
 *   nextColumnIndex: number,
 *   shouldAddNewRow: boolean
 * }} 다음 셀 위치 정보
 * 
 * @description
 * 1. 현재 열에서 다음 열로 이동
 * 2. 마지막 열인 경우 다음 행의 첫 번째 열로 이동
 * 3. autoAddRow가 true이고 마지막 행인 경우 새 행 추가 플래그 설정
 * 
 * @example
 * const result = getNextCellPosition(0, 2, 4, 3, true);
 * // returns { nextRowIndex: 0, nextColumnIndex: 3, shouldAddNewRow: false }
 */
export const getNextCellPosition = (
    currentRowIndex: number,
    currentColumnIndex: number,
    totalColumns: number,
    totalRows: number,
    autoAddRow: boolean,
    columns?: any[]
): { nextRowIndex: number, nextColumnIndex: number, shouldAddNewRow: boolean } => {
    let nextColumnIndex = currentColumnIndex;
    let nextRowIndex = currentRowIndex;
    let shouldAddNewRow = false;

    // 다음 컬럼으로 이동
    if (nextColumnIndex + 1 < totalColumns) {
        nextColumnIndex += 1;
        
        // checkbox 타입이면 건너뛰기
        if (columns && columns[nextColumnIndex]?.type === ValueType.CHECKBOX) {
            // 재귀 호출로 다음 non-checkbox 컬럼 찾기
            return getNextCellPosition(currentRowIndex, nextColumnIndex, totalColumns, totalRows, autoAddRow, columns);
        }
    } else {
        // 다음 행의 첫 번째 컬럼으로 이동
        nextColumnIndex = 0;
        nextRowIndex += 1;

        // 첫 번째 컬럼이 checkbox 타입이면 건너뛰기
        if (columns && columns[nextColumnIndex]?.type === ValueType.CHECKBOX) {
            return getNextCellPosition(nextRowIndex, nextColumnIndex, totalColumns, totalRows, autoAddRow, columns);
        }

        if (autoAddRow && nextRowIndex >= totalRows) {
            shouldAddNewRow = true;
        }
    }

    return { nextRowIndex, nextColumnIndex, shouldAddNewRow };
};


export const getColumnIndex = (columnId: string, columns: { [columnId: string]: BaseColumn }): number => {
    return Object.values(columns).findIndex(col => col.id === columnId);
};

/**
 * 컬럼의 전체 너비를 계산하는 함수
 * 
 * @param column - 너비를 계산할 컬럼 객체
 * @param columns - 전체 컬럼 맵 (key: columnId, value: Column)
 * @returns 계산된 전체 너비
 * 
 * 동작 방식:
 * 1. GROUP 타입 컬럼인 경우:
 *    - 모든 자식 컬럼의 너비를 재귀적으로 합산
 * 2. 일반 컬럼인 경우:
 *    - colspan 값에 따라 연속된 컬럼들의 너비를 합산
 *    - colspan이 없으면 기본값 1 사용
 */
export const calculateColumnWidth = (column: BaseColumn, columns: { [columnId: string]: BaseColumn }): number => {
    // GROUP 타입이고 자식 컬럼이 있는 경우
    if (column.type === ValueType.GROUP && column.children) {
        // 모든 자식 컬럼의 너비를 재귀적으로 합산하여 반환
        return column.children.reduce((sum, child) => {
            return sum + calculateColumnWidth(child, columns);
        }, 0);
    }

    // 일반 컬럼의 경우
    const colspan = column.colspan || 1;  // colspan이 없으면 기본값 1
    // 현재 컬럼부터 colspan 만큼의 컬럼 너비를 합산
    return Object.values(columns)
        .slice(getColumnIndex(column.id, columns), getColumnIndex(column.id, columns) + colspan)
        .reduce((sum, col) => sum + (col.width || 0), 0);
};

/**
 * 그룹 컬럼의 너비를 조정하고 자식 컬럼들의 너비를 비례적으로 조정하는 함수
 * 
 * @param groupColumn - 조정할 그룹 컬럼
 * @param newWidth - 새로운 전체 너비
 * @param columns - 전체 컬럼 맵
 * @returns 조정된 최종 너비
 * 
 * 동작 방식:
 * 1. 그룹 컬럼이 아니거나 자식이 없는 경우 newWidth 반환
 * 2. 현재 전체 너비와 새로운 너비의 차이 계산
 * 3. 자식 컬럼들의 너비를 비례적으로 조정
 * 4. 최소 너비(10px) 제한 적용
 */
export const adjustGroupColumnWidth = (
    groupColumn: Column,
    newWidth: number,
    columns: { [columnId: string]: Column }
): number => {
    if (!groupColumn.children || groupColumn.type !== ValueType.GROUP) {
        return newWidth;
    }

    const currentTotalWidth = calculateColumnWidth(groupColumn, columns);
    const widthDiff = newWidth - currentTotalWidth;

    // 모든 자식 컬럼의 현재 너비 합계 계산
    const totalChildrenWidth = groupColumn.children.reduce(
        (sum, child) => sum + columns[child.id].width,
        0
    );

    // 각 자식 컬럼의 너비를 비례적으로 조정
    groupColumn.children.forEach(child => {
        const ratio = columns[child.id].width / totalChildrenWidth;
        const adjustedWidth = columns[child.id].width + (widthDiff * ratio);
        columns[child.id].width = Math.max(10, adjustedWidth);
    });

    // 조정된 실제 너비 반환
    return groupColumn.children.reduce(
        (sum, child) => sum + columns[child.id].width,
        0
    );
};

/**
 * 계층형 컬럼 구조에서 전체 헤더 높이를 계산하는 함수
 * 
 * @param columns - 컬럼 정보를 담고 있는 객체
 *                 key: columnId, value: Column 객체
 * @returns number - 전체 헤더 높이 (픽셀 단위)
 * 
 * 동작 방식:
 * 1. 최상위 컬럼(parent가 없는)부터 시작하여 재귀적으로 자식 컬럼을 탐색
 * 2. 가장 깊은 계층의 레벨을 찾아 maxLevel에 저장
 * 3. (maxLevel + 1) * ROW_HEIGHT로 전체 높이 계산
 * 
 * 예시:
 * Column A (level 0)
 *   ├─ Child A1 (level 1)
 *   └─ Child A2 (level 1)
 *       └─ Child A2-1 (level 2)
 * Column B (level 0)
 * 
 * maxLevel = 2이므로, 높이 = (2 + 1) * ROW_HEIGHT
 */
export const calculateHeaderHeight = (columns: { [columnId: string]: Column }): number => {
    let maxLevel = 0;
    const processColumn = (column: Column, level: number = 0) => {
        maxLevel = Math.max(maxLevel, level);
        column.children?.forEach(child => processColumn(child, level + 1));
    };

    Object.values(columns)
        .filter(column => !column.parent)
        .forEach(column => processColumn(column));
    return (maxLevel + 1) * ROW_HEIGHT;
};


/**
 * 선명한 픽셀 렌더링을 위한 좌표 보정 함수
 * @param pixel - 보정할 픽셀 좌표 값
 * @param thickness - 선의 두께 (기본값: 1)
 * @returns 보정된 픽셀 좌표 값
 */
export const crispPixel = (pixel: number, thickness: number = 1): number => {
    // 디바이스 픽셀 비율 가져오기
    const dpr = window.devicePixelRatio || 1;

    // 실제 물리적 픽셀 좌표로 변환
    const physicalPixel = pixel * dpr;

    let alignedPixel: number;

    if (thickness % 2 === 0) {
        // 짝수 두께: 정수 픽셀에 정렬
        alignedPixel = Math.round(physicalPixel);
    } else {
        // 홀수 두께: 픽셀 경계에 정렬 (정수 + 0.5)
        alignedPixel = Math.floor(physicalPixel) + 0.5;
    }

    // 논리적 픽셀 좌표로 다시 변환
    return alignedPixel / dpr;
};

/**
 * 사각형을 그리는 함수 (라인만 그리고 배경은 그리지 않음 배경은 drawBackground 함수로 참고)
 * @param ctx Canvas 렌더링 컨텍스트
 * @param x X 좌표
 * @param y Y 좌표
 * @param width 너비
 * @param height 높이
 * @param strokeStyle 테두리 스타일
 * @param lineWidth 선 두께
 */
export const drawRectangle = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    strokeStyle: string,
    lineWidth: number = 1
) => {
    ctx.save();

    // 픽셀 정렬 적용
    const alignedX = crispPixel(x, lineWidth);
    const alignedY = crispPixel(y, lineWidth);
    const alignedWidth = crispPixel(width + x, lineWidth) - alignedX;
    const alignedHeight = crispPixel(height + y, lineWidth) - alignedY;

    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(alignedX, alignedY, alignedWidth, alignedHeight);
    ctx.restore();
};

/**
 * Canvas에 텍스트를 렌더링하는 유틸리티 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param text - 렌더링할 텍스트 문자열
 * @param x - 텍스트를 그릴 X 좌표
 * @param y - 텍스트를 그릴 Y 좌표
 * @param font - 텍스트의 폰트 스타일 (예: "12px Arial")
 * @param color - 텍스트 색상 (예: "#000000")
 * @param textAlign - 텍스트 정렬 방식 (기본값: "left")
 * @param textBaseline - 텍스트 기준선 위치 (기본값: "middle")
 * @param maxWidth - 텍스트의 최대 너비 (선택 사항)
 * 
 * @example
 * drawText(ctx, "Hello", 10, 20, "12px Arial", "#000000", "center", "middle");
 */
export const drawText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    font: string,
    color: string,
    textAlign: CanvasTextAlign = "center",
    textBaseline: CanvasTextBaseline = "middle",
    maxWidth?: number
) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    // 줄바꿈 문자로 텍스트를 분할
    const lines = text.split('\n');

    // 폰트 크기 계산 (예: "12px Arial"에서 12를 추출)
    const fontSize = parseInt(font.match(/\d+/)?.[0] || '12', 10);
    const lineHeight = fontSize * 0.14;

    // 각 줄을 그림
    lines.forEach((line, index) => {
        const lineY = y + (index - (lines.length - 1) / 2) * lineHeight;
        ctx.fillText(line, x, lineY, maxWidth);
    });

    ctx.restore();
};

/**
 * Canvas에 ellipsis가 적용된 텍스트를 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param text - 그릴 텍스트
 * @param x - 텍스트의 X 좌표
 * @param y - 텍스트의 Y 좌표
 * @param font - 폰트 스타일
 * @param color - 텍스트 색상
 * @param textAlign - 텍스트 정렬 방식
 * @param textBaseline - 텍스트 기준선
 * @param maxWidth - 최대 너비 (이 너비를 초과하면 ellipsis 적용)
 * 
 * @description
 * maxWidth를 초과하는 텍스트는 말줄임표(...)를 추가하여 표시합니다.
 */
export const drawTextWithEllipsis = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    font: string,
    color: string,
    textAlign: CanvasTextAlign = "center",
    textBaseline: CanvasTextBaseline = "middle",
    maxWidth?: number
) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    if (!maxWidth) {
        // maxWidth가 없으면 기본 drawText와 동일하게 처리
        ctx.fillText(text, x, y);
        ctx.restore();
        return;
    }

    // 줄바꿈 문자로 텍스트를 분할
    const lines = text.split('\n');

    // 폰트 크기 계산 (예: "12px Arial"에서 12를 추출)
    const fontSize = parseInt(font.match(/\d+/)?.[0] || '12', 10);
    const lineHeight = fontSize * 0.14;

    // 각 줄을 그림
    lines.forEach((line, index) => {
        const lineY = y + (index - (lines.length - 1) / 2) * lineHeight;
        
        // 텍스트 너비 측정
        const textWidth = ctx.measureText(line).width;
        
        if (textWidth <= maxWidth) {
            // 텍스트가 maxWidth 이하이면 그대로 그리기
            ctx.fillText(line, x, lineY);
        } else {
            // 텍스트가 maxWidth를 초과하면 ellipsis 처리
            const ellipsis = '...';
            const ellipsisWidth = ctx.measureText(ellipsis).width;
            
            // ellipsis를 제외한 사용 가능한 너비
            const availableWidth = maxWidth - ellipsisWidth;
            
            // 이진 탐색으로 잘라낼 텍스트 길이 찾기
            let left = 0;
            let right = line.length;
            let bestLength = 0;
            
            while (left <= right) {
                const mid = Math.floor((left + right) / 2);
                const truncatedText = line.substring(0, mid);
                const truncatedWidth = ctx.measureText(truncatedText).width;
                
                if (truncatedWidth <= availableWidth) {
                    bestLength = mid;
                    left = mid + 1;
                } else {
                    right = mid - 1;
                }
            }
            
            // 잘라낸 텍스트 + ellipsis 그리기
            const finalText = line.substring(0, bestLength) + ellipsis;
            ctx.fillText(finalText, x, lineY);
        }
    });

    ctx.restore();
};



/**
 * Canvas에 체크박스를 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 체크박스의 X 좌표
 * @param y - 체크박스의 Y 좌표
 * @param size - 체크박스의 크기 (정사각형)
 * @param isChecked - 체크박스의 선택 상태
 * 
 * @description
 * 1. 체크박스 배경을 그립니다
 * 2. 체크박스 테두리를 그립니다
 * 3. 선택된 상태인 경우 체크마크를 그립니다
 */
export const drawCheckbox = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    isChecked: boolean
) => {
    // 체크박스 배경
    drawBackground(ctx, x, y, size, size, TableStyle.CHECKBOX_FILL);
    // 체크박스 테두리
    drawBorderLine(ctx, x, y, size, size, 1, TableStyle.CHECKBOX_STROKE);

    if (isChecked) {
        ctx.strokeStyle = TableStyle.CHECKMARK;
        ctx.beginPath();
        ctx.moveTo(crispPixel(x + 2), crispPixel(y + 6));
        ctx.lineTo(crispPixel(x + 5), crispPixel(y + 10));
        ctx.lineTo(crispPixel(x + 11), crispPixel(y + 3));
        ctx.stroke();
    }
};



/**
 * Canvas에 테두리 선을 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 테두리를 그릴 시작점의 X 좌표
 * @param y - 테두리를 그릴 시작점의 Y 좌표
 * @param width - 테두리의 너비
 * @param height - 테두리의 높이
 * @param lineWidth - 테두리 선의 두께
 * @param color - 테두리 선의 색상
 * @param alpha - 테두리 선의 투명도 (0~1, 기본값: 1)
 * 
 * @description
 * 1. 선명한 테두리를 위해 픽셀 정렬을 적용합니다
 * 2. 테두리 스타일(색상, 두께, 투명도)을 설정합니다
 * 3. 지정된 크기와 위치에 테두리를 그립니다
 * 
 * @example
 * drawBorderLine(ctx, 10, 10, 100, 50, 1, "#000000", 0.5);
 */
export const drawBorderLine = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    lineWidth: number,
    color: string,
    alpha?: number
) => {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = alpha === undefined ? 1 : alpha;

    // 픽셀 정렬 적용
    const alignedX = crispPixel(x, lineWidth);
    const alignedY = crispPixel(y, lineWidth);
    const alignedWidth = crispPixel(width + x, lineWidth) - alignedX;
    const alignedHeight = crispPixel(height + y, lineWidth) - alignedY;

    ctx.strokeRect(alignedX, alignedY, alignedWidth, alignedHeight);
    ctx.restore();
};



/**
 * Canvas에 배경을 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 배경을 그릴 시작점의 X 좌표
 * @param y - 배경을 그릴 시작점의 Y 좌표
 * @param width - 배경의 너비
 * @param height - 배경의 높이
 * @param fillStyle - 배경 색상
 * @param alpha - 배경의 투명도 (0~1, 기본값: 1)
 * 
 * @description
 * 1. 선명한 배경을 위해 픽셀 정렬을 적용합니다
 * 2. 배경 스타일(색상, 투명도)을 설정합니다
 * 3. 지정된 크기와 위치에 배경을 그립니다
 * 
 * @example
 * drawBackground(ctx, 10, 10, 100, 50, "#ffffff", 0.5);
 */
export const drawBackground = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    fillStyle: string,
    alpha?: number
) => {
    ctx.save();
    ctx.fillStyle = fillStyle;
    ctx.globalAlpha = alpha === undefined ? 1 : alpha;

    // 픽셀 정렬 적용
    const alignedX = crispPixel(x);
    const alignedY = crispPixel(y);
    const alignedWidth = crispPixel(width + x) - alignedX;
    const alignedHeight = crispPixel(height + y) - alignedY;

    ctx.fillRect(alignedX, alignedY, alignedWidth, alignedHeight);
    ctx.restore();
};



export const calculateSummaryColumnMaxDepth = (summaryColumns: SummaryColumn[]): number => {
    return Math.max(...summaryColumns.map(col =>
        col.children ? 1 + calculateSummaryColumnMaxDepth(col.children) : 0
    ));
};


export const drawLine = (
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    lineWidth: number
) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    // 픽셀 정렬 적용
    const alignedX1 = crispPixel(x1, lineWidth);
    const alignedY1 = crispPixel(y1, lineWidth);
    const alignedX2 = crispPixel(x2, lineWidth);
    const alignedY2 = crispPixel(y2, lineWidth);
    ctx.moveTo(alignedX1, alignedY1);
    ctx.lineTo(alignedX2, alignedY2);
    ctx.stroke();
}

/**
 * 셀의 읽기 전용 상태를 확인하는 함수
 * 
 * @param tableId - 테이블 ID
 * @param rowId - 행 ID
 * @param columnId - 열 ID
 * @returns boolean - 읽기 전용 여부
 * 
 * @description
 * 우선순위: 셀 > 행 > 열 순서로 readOnly 속성을 확인
 * 1. 데이터가 없는 경우 읽기 전용으로 처리
 * 2. 셀 레벨의 readOnly 속성 확인
 * 3. 행 레벨의 readOnly 속성 확인
 * 4. 열 레벨의 readOnly 속성 확인
 * 5. 모든 레벨에서 readOnly가 명시되지 않은 경우 false 반환
 */
export const isReadOnly = (
    tableId: string,
    rowId: string,
    columnId: string
): boolean => {
    const row = makeSelectRowById(tableId, rowId)(RootState.getState());
    const column = selectFlattenedColumns(RootState.getState(), tableId).find(col => col.id === columnId);
    const cell = row?.data[columnId];

    if (!row || !column || !cell) return true;

    // 1. 셀 레벨 readOnly 체크
    if (cell.readOnly === undefined || cell.readOnly === false) {
        // 2. 행 레벨 readOnly 체크
        if (row.readOnly === undefined || row.readOnly === false) {
            // 3. 열 레벨 readOnly 체크
            if (column.readOnly === undefined || column.readOnly === false) {
                return false;
            }
            return true;
        }
        return true;
    }
    return true;
};